// Dashboard API response shapes.

export type SiteSummary = {
  id: string;
  domain: string;
  name: string;
  createdAt: number;
  sessions7d: number;
  events7d: number;
  // Total storage weight of this site's recordings, all-time, in bytes
  // (sum of recording_sessions.bytes_stored). Shown as GB on the site card.
  bytesStored: number;
  // Which GB storage plan this site is on (see STORAGE_PLANS).
  storagePlan: StoragePlanId;
  // Set when this site is not owned by the current tenant but shared TO it
  // (GSC-style). The dashboard badges it and read-only shares hide write UI.
  shared?: { fromTenant: string; permission: SharePermission } | null;
};

// --- Cross-tenant sharing (GSC-style) --------------------------------------
export type ShareScope = "account" | "site";
export type SharePermission = "read" | "write";

export type OutgoingShare = {
  id: string;
  granteeEmail: string;
  granteeTenantName: string | null;
  scope: ShareScope;
  siteId: string | null;
  siteDomain: string | null;
  permission: SharePermission;
  createdAt: number;
};
export type IncomingShare = {
  id: string;
  ownerTenantName: string | null;
  scope: ShareScope;
  siteId: string | null;
  siteDomain: string | null;
  permission: SharePermission;
  createdAt: number;
};
export type SharesResponse = { outgoing: OutgoingShare[]; incoming: IncomingShare[] };
export type CreateShareRequest = {
  granteeEmail: string;
  scope: ShareScope;
  siteId?: string | null;      // required when scope='site'
  permission: SharePermission;
};
// Autocomplete suggestion — an EXISTING user/tenant in the system.
export type ShareSuggestion = { email: string; tenantName: string | null };
export type ShareSuggestResponse = { suggestions: ShareSuggestion[] };

// --- Storage plans (GB-based billing, PER SITE) --------------------------
// Recordings are kept forever; sites pay by how much they store. The
// catalogue lives here so dashboard + API agree on the exact numbers. GB are
// decimal (1 GB = 1e9 bytes), matching R2 billing + the cost endpoints.
export type StoragePlanId = "free" | "gb10" | "gb20" | "gb40" | "gb60";

export type StoragePlan = {
  id: StoragePlanId;
  name: string;          // Hebrew display name
  priceIls: number;      // monthly, as the operator stated it
  plusVat: boolean;      // true = price is quoted "+ VAT"
  includedGb: number;    // storage included before overage
  overageIlsPerGb: number; // charged per GB beyond includedGb (0 = free tier)
  best: boolean;         // "best value" highlight
};

// Final storage plans (2026-08-20). Every plan is "up to X GB", so usage
// naturally grows over time unless the customer deletes manually. Overage is a
// flat ₪7 + VAT / GB on ALL plans (priced to nudge an upgrade, not to profit).
// Free 1 GB is forever — self-maintain by hiding, or upgrade to 10 GB+.
export const STORAGE_PLANS: StoragePlan[] = [
  { id: "free", name: "חינם",   priceIls: 0,   plusVat: false, includedGb: 1,  overageIlsPerGb: 0, best: false },
  { id: "gb10", name: "בסיסי",  priceIls: 49,  plusVat: true,  includedGb: 10, overageIlsPerGb: 7, best: false },
  { id: "gb20", name: "צמיחה",  priceIls: 79,  plusVat: true,  includedGb: 20, overageIlsPerGb: 7, best: false },
  { id: "gb40", name: "מקצועי", priceIls: 119, plusVat: true,  includedGb: 40, overageIlsPerGb: 7, best: true  },
  { id: "gb60", name: "עסקי",   priceIls: 199, plusVat: true,  includedGb: 60, overageIlsPerGb: 7, best: false },
];

export function storagePlanById(id: string | null | undefined): StoragePlan {
  return STORAGE_PLANS.find((p) => p.id === id) ?? STORAGE_PLANS[0]!;
}

// --- Page types (owner tagging) ------------------------------------------
// The e-commerce-aware taxonomy the owner tags pages with. `rank` is the
// purchase-funnel progression (0 = non-commercial … 6 = checkout) — a session's
// stage is the max rank of the pages it visited. Hebrew labels are inline
// (product decision: avoid 9-language sprawl for this operator-facing tool).
export type PageType =
  | "home" | "product" | "products" | "categories" | "brands"
  | "cart" | "checkout" | "sale" | "article" | "terms" | "privacy"
  | "account" | "admin" | "other";

export const PAGE_TYPES: Array<{ id: PageType; label: string; rank: number }> = [
  { id: "home",       label: "דף הבית",         rank: 1 },
  { id: "categories", label: "קטגוריות",        rank: 2 },
  { id: "brands",     label: "מותגים",          rank: 2 },
  { id: "products",   label: "דף מוצרים",       rank: 3 },
  { id: "sale",       label: "מבצע",            rank: 3 },
  { id: "product",    label: "דף מוצר",         rank: 4 },
  { id: "cart",       label: "עגלה",            rank: 5 },
  { id: "checkout",   label: "Checkout",        rank: 6 },
  { id: "account",    label: "אזור אישי · מוגן", rank: 0 },
  { id: "admin",      label: "ניהול · פנימי",   rank: 0 },
  { id: "article",    label: "מאמר",            rank: 0 },
  { id: "terms",      label: "תקנון",           rank: 0 },
  { id: "privacy",    label: "מדיניות פרטיות",  rank: 0 },
  { id: "other",      label: "אחר",             rank: 0 },
];

// Page types that are NOT public-visitor pages: the management/back-office
// system (admin) and login-gated personal areas (account). Tagging a page one
// of these marks it "internal" — excluded from public visitor stats, shown
// with the פנימי badge. This is the GUI lever for public-vs-private/ניהול.
export const NON_PUBLIC_PAGE_TYPES: PageType[] = ["admin", "account"];
export function isNonPublicType(t: PageType | null | undefined): boolean {
  return !!t && NON_PUBLIC_PAGE_TYPES.includes(t);
}

export function pageTypeMeta(id: string | null | undefined): { id: PageType; label: string; rank: number } {
  return PAGE_TYPES.find((p) => p.id === id) ?? PAGE_TYPES[PAGE_TYPES.length - 1]!;
}

// Best-effort map from the auto page_kind to a starting page-type guess. The
// owner then confirms or corrects it.
export function pageKindToType(kind: string | null | undefined): PageType {
  switch (kind) {
    case "home": return "home";
    case "product": return "product";
    case "checkout": return "checkout";
    case "content": return "article";
    case "account": return "account";
    case "auth": return "account";
    default: return "other";
  }
}

// Path-aware guess — distinguishes CATEGORY listings from individual PRODUCTS and
// catches brands/categories/sale from the URL structure, which the kind-only
// classifier misses (it lumps everything under /products into "product"). Rule
// of thumb on normalized paths: under /products, ONE ":id" = a category listing,
// TWO+ ":id" = a single product.
export function guessPageType(kind: string | null | undefined, pathname: string): PageType {
  let dec = pathname;
  try { dec = decodeURIComponent(pathname); } catch { /* keep raw */ }
  const low = dec.toLowerCase();
  if (/\/brand/.test(low)) return "brands";
  if (/\/categor|\/category|\/collections?\b/.test(low)) return "categories";
  if (/^\/(products?|shop|store|catalog)\b/.test(low)) {
    const idCount = (pathname.match(/:id/g) || []).length;
    return idCount >= 2 ? "product" : "categories";
  }
  if (/\/sale\b|\/deals?\b|\/promo/.test(low)) return "sale";
  if (/privacy|\/policy|מדיניות.?פרטיות|protection|gdpr/.test(low)) return "privacy";
  if (/\/terms|takanon|תקנון|\/tos\b|תנאי.?שימוש|conditions/.test(low)) return "terms";
  return pageKindToType(kind);
}

// One page row for the tagging editor: the guess, the owner override, and the
// effective type (override ?? guess).
export type PageTypeRow = {
  pageHash: string;
  pathname: string;
  views: number;
  visitors: number;          // distinct sessions that reached this page
  guessedType: PageType;
  overrideType: PageType | null;
  effectiveType: PageType;
};

// --- Owner / provider admin view (cross-tenant) --------------------------
// Only the platform owner (OWNER_TENANT) may read these. One row per site
// across ALL tenants — the "god view" that lets the operator see every
// connected site (theirs + clients') in one place, respecting isolation
// elsewhere. isOwnTenant flags the operator's own sites.
export type AdminSiteRow = {
  siteId: string;
  name: string;
  domain: string;
  tenantId: string;
  tenantName: string;
  isOwnTenant: boolean;
  sessions: number;
  visitors: number;
  storageGb: number;
  storagePlan: StoragePlanId;
};

export type AdminOverview = {
  ownerTenantId: string;
  totals: { tenants: number; sites: number; sessions: number; storageGb: number };
  sites: AdminSiteRow[];
};

// Live storage status for one site: how much it stores, its plan, and the
// resulting monthly bill (base + overage). Powers the storage card + cap bar.
export type SiteStorageStatus = {
  siteId: string;
  bytesStored: number;
  gbUsed: number;          // bytesStored / 1e9, rounded to 3 decimals
  plan: StoragePlan;
  includedGb: number;
  overageGb: number;       // max(0, gbUsed - includedGb), rounded to 3 decimals
  overageIls: number;      // ceil(overageGb) * overageIlsPerGb
  monthlyIls: number;      // priceIls + overageIls (pre-VAT)
  plusVat: boolean;
  pctOfCap: number;        // gbUsed / includedGb * 100 (can exceed 100)
  overCap: boolean;
};

export type PageSummary = {
  pageHash: string;
  pathname: string;
  views: number;
  lastSeen: number;
  kind: PageKind;        // U1 — semantic classification of the pathname
};

// Overview breakdown of traffic by semantic page kind. Computed from
// session_pages within a window. Powers the "what are visitors looking at"
// donut/bar on the Overview + the page-kind filter on Pages.
export type PageKindBreakdown = {
  windowDays: number;
  totalVisits: number;     // total session_page rows in window
  rows: Array<{
    kind: PageKind;
    visits: number;        // session_page rows of this kind
    sessions: number;      // distinct sessions that touched this kind
    distinctPages: number; // distinct paths of this kind
    pct: number;           // 0..100 share of totalVisits
  }>;
};

// Per-site rollup grouped by the FIRST path segment ("/products/123" → "/products").
// Powers the 3D pages-by-route chart on the Overview. Computed from session_pages
// so visits is per-session-page, and avgDwellMs is the average of (last_at - first_at)
// across those rows (zero-dwell rows excluded so single-pageview sessions don't drag
// the average down to zero).
// Site-configured popup. Behavior-triggered intervention.
//
// Triggers — any of these can fire (OR semantics):
//   exitIntent: mouse fast-rising toward the top of the viewport
//   timeOnPageSec: seconds spent on the current page
//   afterRageClick: a rage-click happened in the current session
//   afterDeadClick: a dead-click happened in the current session
//   scrollDepthPct: visitor reached this scroll % of the page
//   afterNPageViews: visitor has loaded ≥ N pages in this session
//
// Targeting — ALL filters must pass (AND semantics):
//   channels: only show to visitors from these source channels
//   returning: only "new", only "returning", or "any"
//   identified: only known identified visitors, or only anonymous
//   converted: only already-converted, or only-not-yet-converted
//   paths.include/exclude: glob URL matches for current page

export type PopupTriggers = {
  exitIntent?: boolean;
  timeOnPageSec?: number | null;
  afterRageClick?: boolean;
  afterDeadClick?: boolean;
  scrollDepthPct?: number | null;
  afterNPageViews?: number | null;
  // R2 — fire when the live Abandonment-Risk score (0-100) crosses this
  // threshold. This is the predictive trigger: instead of waiting for the
  // crude last-moment exit-intent, the popup fires the instant the composite
  // intent-to-leave model says the visitor is about to go.
  riskScore?: number | null;
};

export type PopupTargeting = {
  channels?: string[];
  returning?: "new" | "returning" | "any";
  identified?: "yes" | "no" | "any";
  converted?: "yes" | "no" | "any";
  paths?: { include?: string[]; exclude?: string[] };
};

// AI credentials. Per-tenant, per-provider. Returned from GET hides the
// actual key — only sends a masked preview so the operator can confirm
// which one is on file without exposing it on inspection.
export type AiProvider = "openai" | "anthropic" | "gemini";

export type TenantAiCredential = {
  provider: AiProvider;
  keyMasked: string;       // e.g. "sk-...AbCd"
  model: string | null;
  hasKey: boolean;
  updatedAt: number;
  // Where the usable key comes from. "own" = this tenant's own key; "granted" =
  // a key another tenant shared with this user; "platform" = the "AI through us"
  // fallback (owner's key, metered + billed to this tenant). Anything other than
  // "own" means the tenant can run AI without having entered a key of its own.
  source?: "own" | "granted" | "platform";
};

// --- AI usage metering + billing -------------------------------------------
// Rough public list prices (USD per 1M tokens) for the models we default to,
// used to ESTIMATE cost per call for billing decisions. Not authoritative —
// the operator sets their own markup; this is the "what it cost me" baseline.
export const AI_MODEL_PRICING: Record<string, { in: number; out: number }> = {
  // OpenAI
  "gpt-4o-mini": { in: 0.15, out: 0.60 },
  "gpt-4o": { in: 2.50, out: 10.0 },
  "gpt-4.1-mini": { in: 0.40, out: 1.60 },
  // Anthropic
  "claude-haiku-4-5-20251001": { in: 1.0, out: 5.0 },
  "claude-3-5-haiku-20241022": { in: 0.80, out: 4.0 },
  "claude-sonnet-4-5": { in: 3.0, out: 15.0 },
  // Google
  "gemini-2.0-flash-exp": { in: 0.10, out: 0.40 },
  "gemini-1.5-flash": { in: 0.075, out: 0.30 },
};
// Per-provider fallback when the exact model isn't in the table.
const AI_PROVIDER_FALLBACK: Record<string, { in: number; out: number }> = {
  openai: { in: 0.15, out: 0.60 },
  anthropic: { in: 1.0, out: 5.0 },
  gemini: { in: 0.10, out: 0.40 },
};

export function estimateAiCostUsd(
  provider: string, model: string | null, inputTokens: number, outputTokens: number
): number {
  const p = (model && AI_MODEL_PRICING[model]) || AI_PROVIDER_FALLBACK[provider] || { in: 0, out: 0 };
  const cost = (inputTokens / 1_000_000) * p.in + (outputTokens / 1_000_000) * p.out;
  return Math.round(cost * 1_000_000) / 1_000_000; // round to 6 decimals (micro-dollars)
}

export type AiUsageFeature = "advisor" | "heatmap-insights" | "generate-popup" | "site-profile";

// One audit-log row (as returned to the dashboard).
export type AiUsageRow = {
  id: number;
  ts: number;
  callerTenantId: string;
  callerUserId: string | null;
  callerEmail: string | null;
  credentialTenantId: string;
  provider: AiProvider;
  model: string | null;
  feature: AiUsageFeature;
  siteId: string | null;
  inputTokens: number;
  outputTokens: number;
  estCostUsd: number;
  status: "ok" | "error";
  error: string | null;
};

// Aggregated usage, grouped by a dimension (provider / feature / consumer).
export type AiUsageBucket = {
  key: string;            // provider, feature, or caller identity
  label: string;          // human label (email / provider / feature)
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estCostUsd: number;
};

export type AiUsageSummary = {
  days: number;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  byProvider: AiUsageBucket[];
  byFeature: AiUsageBucket[];
  // Only on the "provided" (billing) view — usage grouped by who consumed MY keys.
  byConsumer?: AiUsageBucket[];
  recent: AiUsageRow[];
};

// A credential grant — I let this user work with my key for this provider.
export type AiCredentialGrant = {
  id: string;
  ownerTenantId: string;
  provider: AiProvider;
  granteeUserId: string;
  granteeEmail: string | null;
  createdAt: number;
};

export type CreateAiGrantRequest = {
  email: string;          // the user to grant (must be an existing user)
  provider: AiProvider;
};

export type AiGeneratePopupRequest = {
  provider: AiProvider;
  // Free-text prompt describing the popup the operator wants
  prompt: string;
  // Existing context (current popup content / triggers) — helps AI refine
  // rather than start from scratch.
  context?: {
    currentTitle?: string;
    currentBody?: string;
    audience?: string;
    siteDomain?: string;
  };
};

export type AiGeneratePopupResponse = {
  variations: Array<{
    title: string;
    body: string;
    ctaLabel: string;
    ctaHref: string;
  }>;
};

export type PopupType = "modal" | "slide-in" | "bar";

// P1 — premium entrance animations. All pure-CSS/transform (compositor-friendly,
// no layout work) so they load fast and never jank, matching the landing 3D hero
// discipline. The widget falls back to "rise" for unknown values.
export type PopupAnimation =
  | "rise"      // default — fade + lift + subtle scale
  | "spring"    // overshoot bounce-in (springy cubic-bezier)
  | "tilt3d"    // 3D perspective flip-in from a tilt
  | "scale"     // scale-from-cursor (transform-origin at the pointer)
  | "glow"      // rise + pulsing accent glow ring
  | "confetti"  // rise + a short confetti burst behind the card
  | "slide"     // slide in from the side
  | "bounce"    // drop + bounce settle
  | "flip"      // 3D card flip on the Y axis
  | "zoom"      // punchy zoom-in from small
  | "swing"     // pendulum swing-in from the top
  | "jelly";    // wobbly elastic squash-and-stretch

export const POPUP_ANIMATIONS: PopupAnimation[] = [
  "rise", "spring", "tilt3d", "scale", "glow", "confetti",
  "slide", "bounce", "flip", "zoom", "swing", "jelly",
];

// A LOOPING attention effect that plays AFTER the entrance settles — the bit
// that keeps pulling the eye back so the visitor actually engages. Optional.
export type PopupAttention =
  | "none"
  | "float"     // gentle continuous up-down hover
  | "wiggle"    // periodic playful tilt-wiggle
  | "shine"     // a light sweep glides across the card every few seconds
  | "heartbeat" // subtle double-thump scale
  | "breathe";  // slow grow/shrink, calm and alive

export const POPUP_ATTENTIONS: PopupAttention[] = [
  "none", "float", "wiggle", "shine", "heartbeat", "breathe",
];

// Animated character mascots — original, cartoon-style SVG figures (drawn by
// us → no copyright issues) that bob, blink and wave to catch the eye and pull
// the visitor back to the message. Rendered as a lively animated avatar.
export type PopupMascot =
  | "none"
  | "parrot"   // 🦜 tropical parrot — wing-flap, crest bounce, talking beak
  | "monkey"   // 🐵 cute monkey in a fez hat — hat tips, tassel swings, waves
  | "stylist"  // ✂️ stylist — shades glint, scissors snip-snip
  | "lady"     // 💃 elegant woman — flowing hair, dangling earrings, lashes
  | "fox"      // 🦊 head-tilting fox
  | "robot"    // 🤖 friendly assistant bot
  | "cat"      // 🐱 blinking cat
  | "panda"    // 🐼 waving panda
  | "ghost";   // 👻 bobbing friendly ghost

export const POPUP_MASCOTS: PopupMascot[] = [
  "none", "parrot", "monkey", "stylist", "lady", "fox", "robot", "cat", "panda", "ghost",
];

// Color palettes for the mascots — match the character to the site's brand.
// Each palette is [main, light, dark]; the SVGs read them as CSS vars with
// sensible per-character fallbacks, so "none" keeps the native art colors.
export type MascotPaletteId = "brand" | "sunset" | "royal" | "rose" | "ocean" | "gold" | "night";
export const MASCOT_PALETTES: Array<{ id: MascotPaletteId; label: string; colors: [string, string, string] }> = [
  { id: "brand",  label: "טורקיז",  colors: ["#14b8a6", "#2dd4bf", "#0f766e"] },
  { id: "sunset", label: "שקיעה",   colors: ["#f97316", "#fb923c", "#c2410c"] },
  { id: "royal",  label: "סגול",    colors: ["#7c3aed", "#a78bfa", "#5b21b6"] },
  { id: "rose",   label: "ורוד",    colors: ["#ec4899", "#f9a8d4", "#be185d"] },
  { id: "ocean",  label: "כחול",    colors: ["#0ea5e9", "#7dd3fc", "#0369a1"] },
  { id: "gold",   label: "זהב",     colors: ["#f59e0b", "#fcd34d", "#b45309"] },
  { id: "night",  label: "כהה",     colors: ["#334155", "#64748b", "#1e293b"] },
];

// Entrance sound — synthesized with WebAudio (no files, ~0 bytes). Plays when
// the popup appears; silently skipped when the browser blocks audio.
export type PopupSound = "none" | "chime" | "pop" | "tada" | "whoosh" | "sparkle";
export const POPUP_SOUNDS: PopupSound[] = ["none", "chime", "pop", "tada", "whoosh", "sparkle"];

export type PopupContent = {
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  theme?: "light" | "dark" | "accent";
  // P1 — entrance animation. Optional; widget defaults to "rise".
  animation?: PopupAnimation;
  // A looping attention effect after entrance (float/wiggle/shine…). Keeps the
  // visitor's eye on the message. Optional; default none.
  attention?: PopupAttention;
  // Avatar — a friendly face makes the message feel human and lifts engagement.
  // Either an emoji ("👋", "🎁", "🧑‍💻") or an image URL. Rendered as a circular
  // avatar with a subtle float + a live "online" dot.
  avatar?: string;
  avatarLabel?: string;       // small caption under the message, e.g. "נועה · צוות התמיכה"
  // An animated character mascot (parrot/monkey/fox…) — the eye-catcher. When
  // set it renders a lively animated SVG figure in place of the emoji avatar,
  // presented HERO-style: floating above the card with a glow halo and a
  // burst ring on entrance, while the content staggers in below.
  mascot?: PopupMascot;
  // Recolor the mascot to match the site's brand ("none"/undefined keeps the
  // character's native colors).
  mascotPalette?: MascotPaletteId;
  // Synthesized entrance sound (chime/pop/tada/whoosh/sparkle).
  sound?: PopupSound;
  // "Make them stay" layer (all optional):
  couponCode?: string;        // a promo/benefit code shown with a copy button
  ctaPulse?: boolean;         // gently pulse the CTA to pull the eye to it
  countdownMinutes?: number;  // urgency timer counting down from N minutes
};

export type SitePopup = {
  id: string;
  siteId: string;
  name: string;
  enabled: boolean;
  popupType: PopupType;
  triggers: PopupTriggers;
  targeting: PopupTargeting;
  content: PopupContent;
  cooldownHours: number;
  createdAt: number;
  updatedAt: number;
  // Live rollups attached on the list endpoint:
  shown?: number;
  clicked?: number;
  converted?: number;
  ctr?: number;
  cvr?: number;
  // R6 — Rescued vs Lost. Of the sessions that SAW this popup, how many went
  // on to convert (rescued) vs not (lost). This is the ROI ledger — it proves
  // the intervention worked and lets the operator tune thresholds.
  rescued?: number;
  lost?: number;
  recoveryRate?: number;   // 0..100 = rescued / shown
};

// Precision #3 — popup placement intelligence. Data-grounded answers to
// "how and where should I place popups", computed from the risk timeline +
// conversion + existing popup outcomes. All derived from data we already have.
export type PopupIntel = {
  windowDays: number;
  // The risk threshold to fire at — the lowest band where sessions become
  // very likely to leave without converting (with enough volume to matter).
  recommendedRiskThreshold: number | null;
  // Non-conversion by peak-risk band → shows WHERE intent-to-leave concentrates.
  riskBands: Array<{
    min: number; max: number;
    sessions: number;
    notConverted: number;
    notConvRate: number;   // 0..100
  }>;
  // Best pages to place a popup on: high recoverable volume (traffic × abandon).
  targetPages: Array<{
    pageHash: string;
    pathname: string;
    sessions: number;
    notConvRate: number;   // 0..100
    avgRiskPeak: number;
    recoverable: number;   // sessions that hit the page, elevated risk, didn't convert
  }>;
  // Which trigger actually rescues best (from live popup_impressions).
  triggerPerf: Array<{
    trigger: string;
    shown: number;
    rescued: number;
    recoveryRate: number;  // 0..100
  }>;
};

// --- R-core — server-side risk→action rules -------------------------------
// WHEN risk ≥ minRisk AND <targeting> THEN <actions>, executed OFF-SITE after
// a grace delay (the visitor may still convert on their own). The aggregator
// evaluates rules on incoming `risk` events; the actions worker executes.

export type ActionKind = "webhook" | "email" | "voice" | "payment_link";

// Targeting — ALL filters must pass (AND semantics), mirroring PopupTargeting.
// identified defaults to "yes" for contact channels (email/voice need a person).
export type ActionTargeting = {
  channels?: string[];                              // source channels (direct/paid/…)
  identified?: "yes" | "no" | "any";
  paths?: { include?: string[]; exclude?: string[] }; // glob match on TEMPLATE path
  pageKinds?: string[];                             // e.g. ["checkout","cart"]
};

// One action a rule performs. A rule can carry several (e.g. webhook + email).
export type ActionConfig =
  | {
      kind: "webhook";
      // POSTs a JSON payload (site/rule/session/visitor/risk/traits). If
      // secret is set we sign the body: X-HotMaps-Signature: hex(HMAC-SHA256).
      url: string;
      secret?: string;
    }
  | {
      kind: "email";
      // R4 — MailOS recovery email. {{name}} / {{email}} placeholders are
      // resolved from the identified traits at send time.
      subject: string;
      bodyHtml: string;
      fromName?: string;
      ctaUrl?: string;
      ctaLabel?: string;
    }
  | {
      kind: "voice";
      // R5 — CALLSAI outbound TTS call. {{name}} placeholder resolved from
      // identified traits; the destination is the visitor's phone trait.
      script: string;
      lang?: string;     // he | en | ar | ru | fr | es | de | pt | it | tr | uk | hi | am
    }
  | {
      kind: "payment_link";
      // R7 — PAYWAYS one-click recovery. Mints a hosted checkout link for
      // the visitor's abandoned cart (session cart_json, reported via
      // hotmaps.track("cart", {value, currency, label})) and emails it.
      // Placeholders: {{name}} / {{email}} / {{amount}}.
      subject: string;
      bodyHtml: string;
      fromName?: string;
      ctaLabel?: string;         // button text; default "לתשלום" side handled in UI
      // Site owner's PAYWAYS pay-key (ddcpay_…) — the charge lands in THEIR
      // org. Absent → the worker's default PAYWAYS_KEY (owner's own sites).
      payKey?: string;
      // Fallback amount when the session has no cart snapshot. If neither
      // exists the action is skipped (nothing to charge for).
      fixedAmount?: number;
      currency?: string;         // default ILS
      description?: string;      // charge description; default rule name
    };

export type ActionRule = {
  id: string;
  siteId: string;
  name: string;
  enabled: boolean;
  minRisk: number;            // 0-100
  targeting: ActionTargeting;
  actions: ActionConfig[];
  delayMinutes: number;       // grace period before executing
  cooldownHours: number;      // per-visitor per-rule quiet period
  createdAt: number;
  updatedAt: number;
  // Rollups attached on the list endpoint (R6-style outcome ledger):
  triggered?: number;         // outcome rows (any status)
  sent?: number;
  failed?: number;
  skipped?: number;
  rescued?: number;           // session converted at/after the action fired
  lost?: number;
  recoveryRate?: number;      // 0..100 = rescued / sent
};

export type ActionOutcomeStatus =
  | "queued" | "sent" | "failed"
  | "skipped_converted" | "skipped_no_contact"
  | "skipped_targeting" | "skipped_disabled";

// One ledger row for the outcomes table on the dashboard.
export type ActionOutcomeRow = {
  id: number;
  ruleId: string;
  ruleName: string | null;
  sessionId: string;
  visitorId: string;
  actionKind: ActionKind;
  riskScore: number;
  triggeredAt: number;
  executeAfter: number;
  status: ActionOutcomeStatus;
  executedAt: number | null;
  detail: Record<string, unknown> | null;
  rescued: boolean;           // session converted at/after triggered_at
  identifiedEmail: string | null;
};

// --- Growth Advisor — active, content-aware recommendation engine ----------
// The leap from "observe + show heatmaps" to "analyze the client's OWN site
// + its behavior data → tell them exactly what to fix and hand them a
// ready-to-enable popup drafted from their real content". One AI call fuses:
//   analytics (hotspots, dead/rage clusters, scroll, risk bands, conversion
//   gap, personas, page-performance) + the page's actual DOM text (from the
//   stored FullSnapshot) → findings + a drafted popup toward a chosen goal.

export type AdvisorGoal =
  | "abandonment"   // stop them leaving
  | "cart"          // push cart/checkout completion
  | "contact";      // drive lead / contact / booking

export const ADVISOR_GOALS: AdvisorGoal[] = ["abandonment", "cart", "contact"];

// One diagnosis item — grounded in a specific data signal, with a concrete fix.
export type AdvisorFinding = {
  severity: "info" | "warning" | "critical";
  area: "ui" | "ux" | "content" | "conversion" | "performance";
  title: string;
  evidence: string;     // what the DATA shows (cites the numbers we fed it)
  suggestion: string;   // the specific change to make
};

// A ready-to-enable popup the advisor drafted FROM the client's own content.
// Shape maps 1:1 onto POST /sites/:id/popups so the dashboard "Create" button
// is a single click — no rewriting.
export type AdvisorPopupDraft = {
  goal: AdvisorGoal;
  rationale: string;               // why THIS popup, tied to the findings
  name: string;
  popupType: PopupType;
  content: PopupContent;           // authored from the page's real copy/products
  triggers: PopupTriggers;         // includes riskScore threshold when relevant
  targeting: PopupTargeting;       // path-scoped to where it matters
  targetPathname: string | null;   // human-readable page it should run on
};

export type GrowthAdvisorRequest = {
  pageHash: string;
  goal: AdvisorGoal;
  provider: AiProvider;
  viewport?: "desktop" | "tablet" | "mobile";
};

export type GrowthAdvisorResponse = {
  pathname: string;
  goal: AdvisorGoal;
  // Honest signal so the UI can warn "thin data" instead of over-promising.
  dataQuality: "rich" | "some" | "thin";
  hadContent: boolean;             // did we have a real DOM snapshot to read?
  findings: AdvisorFinding[];
  popupDraft: AdvisorPopupDraft | null;
  // Compact echo of the signals we fed the model, for the "why" panel.
  signals: {
    sessions: number;
    convRate: number;
    exitRate: number;
    avgRiskPeak: number;
    topDeadRage: Array<{ kind: "dead" | "rage"; label: string | null; visitors: number }>;
  };
  // Value-loop billing: was this analysis free (teaser) or charged to credits.
  // Absent when the tenant used its own AI key (they pay the provider directly).
  billing?: {
    usingPlatformKey: boolean;
    charged: number;        // credits deducted (0 = free teaser)
    free: boolean;          // true = ran on the free monthly allotment
    creditCost: number;     // what it costs beyond free
    freePerMonth: number;   // size of the free teaser
  };
};

// --- Site Intelligence — whole-site AI understanding -----------------------
// A persisted profile: what this business IS, its catalog, its audience, and
// prioritized site-wide actions — grounded in the real page content AND the
// visitor traffic. Generated on demand (costs tokens), cached, refreshable.

export type SiteType =
  | "ecommerce" | "saas" | "lead_gen" | "content" | "service" | "booking"
  | "marketplace" | "portfolio" | "education" | "nonprofit" | "other";

// One mapped product or service, tied to a real page + its traffic signal.
export type SiteCatalogItem = {
  name: string;
  kind: "product" | "service";
  pathname: string | null;      // the page it lives on, if identified
  price: string | null;         // as seen in the content (e.g. "₪249")
  interest: "high" | "medium" | "low"; // from real visitor traffic to its page
  note: string | null;
};

// A prioritized, site-wide improvement action.
export type SiteAction = {
  title: string;
  area: "conversion" | "ux" | "ui" | "content" | "catalog" | "acquisition" | "retention" | "performance";
  rationale: string;            // grounded in data + content
  impact: "high" | "medium" | "low";
  effort: "high" | "medium" | "low";
  targetPathname: string | null;
};

export type SiteProfile = {
  siteType: SiteType;
  siteTypeConfidence: "high" | "medium" | "low";
  businessSummary: string;      // plain-language: what this site sells / does
  audienceSummary: string;      // who the visitors are (tied to personas/sources)
  catalog: SiteCatalogItem[];   // mapped products/services
  keyPages: Array<{ pathname: string; role: string }>; // home, top converter, leak point…
  strengths: string[];
  actions: SiteAction[];        // prioritized site-wide improvements
  // Echo of the signal pack we grounded the model on (for the "why" panel).
  signals: {
    pagesAnalyzed: number;
    sessions: number;
    conversions: number;
    topChannels: Array<{ channel: string; sessions: number }>;
    kinds: Array<{ kind: PageKind; pct: number }>;
    revenue: number | null;
  };
};

// What the profile endpoint returns (cached wrapper).
export type SiteProfileResponse = {
  profile: SiteProfile | null;   // null if never generated
  generatedAt: number | null;
  provider: AiProvider | null;
  model: string | null;
  stale: boolean;                // generated > 14 days ago (cosmetic hint)
};

export type GenerateSiteProfileRequest = {
  provider: AiProvider;
};

// --- Growth Advisor: site-wide auto-scan -----------------------------------
// Free, instant, no AI key: ranks every page by an "opportunity score" derived
// only from the data (recoverable high-risk non-converters × traffic, plus
// frustration and exit pressure). Answers "which pages should I fix FIRST"
// across the whole site; each row links to the per-page AI deep-dive.

export type AdvisorTopIssue =
  | "abandonment"      // high-risk visitors leaving without converting
  | "high_exit"        // this page is where sessions end
  | "frustration"      // rage clusters
  | "low_conversion"   // traffic but weak conversion
  | "healthy";         // nothing obviously wrong

export type AdvisorScanRow = {
  pageHash: string;
  pathname: string;
  kind: PageKind;
  sessions: number;
  convRate: number;        // 0..100
  exitRate: number;        // 0..100
  avgRiskPeak: number;     // 0..100
  recoverable: number;     // high-risk (>=60) non-converting sessions
  rageVisitors: number;    // distinct visitors in rage clusters on this page
  hasSnapshot: boolean;    // is a DOM snapshot available for the AI deep-dive?
  topIssue: AdvisorTopIssue;
  opportunityScore: number; // 0..100 — higher = more money left on the table
};

export type AdvisorScanResponse = {
  days: number;
  scanned: number;         // pages considered
  rows: AdvisorScanRow[];   // ranked, best opportunity first
};

// --- Deterministic popup suggestions ("the method") ----------------------
// Turns the rule-based opportunity scan into ready-to-activate popup configs —
// no AI needed. This is the engine that runs on incoming data (client now, cron
// next) so the system proposes interventions on its own. Gemini can later polish
// the copy, but the trigger/type/targeting come from the behaviour data.
export type PopupSuggestion = {
  pageHash: string;
  pathname: string;
  opportunityScore: number;
  reason: string;            // the deterministic rationale (cites the numbers)
  name: string;
  popupType: PopupType;
  triggers: PopupTriggers;
  targeting: PopupTargeting;
  content: PopupContent;
  cooldownHours: number;
};

// A path glob the tracker can match against real URLs — the scan pathname is
// normalized (":id" segments), so we widen at the first :id.
function pathGlob(pathname: string): string {
  const out: string[] = [];
  for (const s of pathname.split("/")) {
    if (s === ":id") { out.push("*"); return (out.join("/") || "/"); }
    out.push(s);
  }
  const g = out.join("/") || "/";
  return g === "/" ? "/" : g + "*";
}

export function suggestPopups(rows: AdvisorScanRow[]): PopupSuggestion[] {
  const out: PopupSuggestion[] = [];
  for (const r of rows.filter((x) => x.opportunityScore >= 12).slice(0, 8)) {
    const glob = pathGlob(r.pathname);
    const paths = { include: [glob] };
    let s: Omit<PopupSuggestion, "pageHash" | "pathname" | "opportunityScore"> | null = null;

    if (r.kind === "checkout") {
      s = {
        reason: `צ'קאאוט · שיא-סיכון ${Math.round(r.avgRiskPeak)} · ${r.recoverable} ניתנים לשחזור`,
        name: `שחזור צ'קאאוט · ${glob}`,
        popupType: "modal",
        triggers: { exitIntent: true, riskScore: 55 },
        targeting: { paths },
        content: { title: "רגע לפני שאתה עוזב 🛒", body: "ההזמנה שלך שמורה. צריך עזרה להשלים? צוות התמיכה כאן עכשיו.", ctaLabel: "להשלמת ההזמנה", ctaHref: r.pathname, theme: "accent", animation: "spring", ctaPulse: true },
        cooldownHours: 24,
      };
    } else if (r.kind === "product") {
      s = {
        reason: `דף מוצר · ${Math.round(r.exitRate)}% יציאה`,
        name: `עזרה בבחירה · ${glob}`,
        popupType: "slide-in",
        triggers: { exitIntent: true, timeOnPageSec: 25 },
        targeting: { paths },
        content: { title: "מתלבטים? 🤔", body: "נעזור לכם לבחור נכון — ספרו לנו מה אתם מחפשים ונמצא בדיוק את זה.", ctaLabel: "דברו איתנו", ctaHref: "/contactus", theme: "light", animation: "rise" },
        cooldownHours: 48,
      };
    } else if (r.topIssue === "frustration") {
      s = {
        reason: `תסכול · ${r.rageVisitors} גולשים בלחיצות-זעם`,
        name: `עזרה מיידית · ${glob}`,
        popupType: "bar",
        triggers: { afterRageClick: true, riskScore: 50 },
        targeting: { paths },
        content: { title: "משהו לא עובד? 🛠️", body: "נראה שנתקעתם — אנחנו כאן לעזור מיד.", ctaLabel: "פנייה מהירה", ctaHref: "/contactus", theme: "dark", animation: "glow" },
        cooldownHours: 12,
      };
    } else {
      s = {
        reason: `${Math.round(r.exitRate)}% יציאה · ${r.recoverable} ניתנים לשחזור`,
        name: `מניעת נטישה · ${glob}`,
        popupType: "slide-in",
        triggers: { exitIntent: true },
        targeting: { paths },
        content: { title: "לפני שתלכו 👋", body: "יש לנו מבצעים חמים היום — שווה הצצה לפני שאתם עוזבים.", ctaLabel: "למבצעים", ctaHref: "/sale", theme: "accent", animation: "rise", ctaPulse: true },
        cooldownHours: 48,
      };
    }
    out.push({ pageHash: r.pageHash, pathname: r.pathname, opportunityScore: r.opportunityScore, ...s });
  }
  return out;
}

// Discovered field — one row per unique fieldSig observed on a site.
// Populates the Fields page where the owner tags each as email / name /
// customer_id / phone / ignore. samples is a small carousel of recent
// values (truncated) for the owner to recognize the field.
export type DiscoveredFieldRow = {
  fieldSig: string;
  fieldName: string | null;
  fieldType: string | null;
  fieldPlaceholder: string | null;
  fieldLabel: string | null;
  pathname: string | null;
  observations: number;
  lastObservedAt: number;
  role: FieldRole | null;
  samples: string[]; // up to 3 truncated recent values
  // Canonical grouping (server-side tidy-up). The same LOGICAL field appears
  // under many raw signatures — page-builders mint a random name per form
  // instance (form_fields[field_a740460]) and the sig embeds the pathname, so
  // one "Email" field explodes into dozens of rows. The server merges them:
  variants?: number;        // raw signatures folded into this row
  pages?: number;           // distinct pages the field was seen on
  memberSigs?: string[];    // every folded signature — role-tagging applies to ALL
  // Hidden tracking pseudo-fields (fb pixel cd[...]/ap[...], audff[...], and
  // other label-less machine names). Real visitors never see these; the UI
  // collapses them out of the way instead of drowning the list.
  junk?: boolean;
};

export type FieldRole = "email" | "name" | "customer_id" | "phone" | "ignore";

// One row per distinct visitor of a site. Drives the Visitors list — the
// "people layer" above the per-session view. A visitor is the same browser
// over time (tracker cookie + localStorage), so this is "humans counted".
// Identified email/name come from either identify() traits or auto-extract.
export type VisitorRow = {
  visitorId: string;
  identifiedUserId: string | null;
  identifiedEmail: string | null;
  identifiedName: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  sessions: number;
  converted: boolean;
  convertedAt: number | null;     // first conversion ts across their sessions
  conversionPath: string | null;  // which path triggered it
  isInternal: boolean;            // any of their sessions hit an internal-path rule
  signals: number;
  // Soft attribution of "where did this person first come from?". Read from
  // the visitor's earliest session.
  firstChannel: string | null;
  firstReferrerHost: string | null;
  // Current lifecycle stage (visitor→interested→cart→checkout→purchase→returning),
  // most-recent by stage_at. null until the site reports a stage/canonical event.
  lifecycleStage: string | null;
  // Sale detection — value of the first recorded sale, when the site reports
  // purchases via hotmaps.track("purchase", {value}).
  conversionValue: number | null;
  conversionCurrency: string | null;
  conversionLabel: string | null;
  // Did the system get to OFFER this visitor anything? Rolled up from
  // popup_impressions across all their sessions: shown → the offer reached
  // them; clicked/converted → they engaged / it worked.
  offersShown: number;
  offersClicked: number;
  offersConverted: number;
  // Behavior persona — rule-based archetype from the visitor's aggregates.
  persona: Persona;
};

// --- Behavior personas -----------------------------------------------------
// One archetype per visitor, derived ONLY from aggregates we already store.
// Order of checks matters (first match wins) — buyer beats everything, then
// friction states, then engagement styles.
export type Persona =
  | "buyer"        // converted (best signal there is)
  | "frustrated"   // rage/dead/errors — they're fighting the UI
  | "hesitant"     // came close to leaving (high risk peak), didn't convert
  | "loyal"        // keeps coming back (3+ sessions)
  | "researcher"   // deep dwell + deep scroll — reading, comparing
  | "scanner"      // many pages, seconds each — hunting for something
  | "bouncer"      // one page, gone in seconds
  | "casual";      // none of the above

export const PERSONAS: Persona[] = [
  "buyer", "frustrated", "hesitant", "loyal", "researcher", "scanner", "bouncer", "casual",
];

export function classifyPersona(v: {
  sessions: number;
  converted: boolean;
  totalDwellMs: number;   // SUM(duration_ms)
  totalPages: number;     // SUM(page_count)
  maxScrollPct: number;   // MAX(max_scroll_pct)
  riskPeak: number;       // MAX(risk_peak)
  signals: number;        // RAGE clicks only. Dead clicks are whitespace-
                          // click noise (819 dead vs 107 rage on live ncrm
                          // data — the same false positive page-health had
                          // to engineer around), and JS/network errors are
                          // the SITE's problem, not the visitor's mood.
}): Persona {
  if (v.converted) return "buyer";
  if (v.signals >= 2) return "frustrated";
  if (v.riskPeak >= 70) return "hesitant";
  if (v.sessions >= 3) return "loyal";
  const pages = Math.max(1, v.totalPages);
  const dwellPerPageS = v.totalDwellMs / pages / 1000;
  if (pages >= 4 && dwellPerPageS >= 25 && v.maxScrollPct >= 60) return "researcher";
  if (pages >= 5 && dwellPerPageS < 10) return "scanner";
  if (v.sessions === 1 && pages <= 1 && v.totalDwellMs < 20_000) return "bouncer";
  return "casual";
}

// Per-persona counts for the Visitors page breakdown card.
export type PersonaBreakdown = {
  days: number;
  total: number;
  rows: Array<{ persona: Persona; count: number; pct: number }>;
};

// --- Interest detection (per specific product/service page) ----------------
// Grouped by RAW pathname (not template hash) so /product/red-shoes and
// /product/blue-shoes rank separately — this is "which THING interests
// people", not "how does the product template perform".
export type ProductInterestRow = {
  pathname: string;
  kind: PageKind;
  uniqueVisitors: number;
  repeatVisitors: number;      // visitors who came back to this page in another session
  avgDwellMs: number;
  identifiedVisitors: number;  // how many of them we know by name/email
  interestedEmails: string[];  // up to 5 identified emails, most recent first
  interestScore: number;       // visitors × dwell × repeat-boost, normalized 0-100
};

export type ProductInterest = {
  days: number;
  rows: ProductInterestRow[];
};

// --- Page success scoring ---------------------------------------------------
// Per TEMPLATE page: does traffic that touches this page end up converting,
// engaging, or leaving? successScore blends conversion participation,
// engagement and (inverted) exit/risk so pages can be ranked.
export type PagePerformanceRow = {
  pageHash: string;
  pathname: string;            // template path (/product/:id)
  kind: PageKind;
  sessions: number;
  convertedSessions: number;
  convRate: number;            // 0..100
  avgDwellMs: number;
  exitRate: number;            // 0..100 — sessions where this was the LAST page
  avgRiskPeak: number;         // 0..100 over sessions touching the page
  revenue: number;             // SUM(conversion_value) of converted sessions touching it
  successScore: number;        // 0..100 composite
};

export type PagePerformance = {
  days: number;
  rows: PagePerformanceRow[];
};

// U2 — Customer Journey. The full story of ONE visitor across all their
// sessions: where they came from, what kinds of pages they looked at and for
// how long, the moment they converted, what they filled in, and how close
// they came to leaving. Composed from existing data (recording_sessions +
// session_pages + field_observations + site_field_roles) — no new capture.
export type CustomerJourney = {
  visitorId: string;
  identifiedUserId: string | null;
  identifiedEmail: string | null;
  identifiedName: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  sessions: number;
  // First touch — earliest session's acquisition.
  firstChannel: string | null;
  firstReferrerHost: string | null;
  firstLandingPath: string | null;
  // Conversion.
  converted: boolean;
  convertedAt: number | null;
  conversionPath: string | null;
  // Sale value when reported via hotmaps.track("purchase", {value, label}).
  conversionValue: number | null;
  conversionCurrency: string | null;
  conversionLabel: string | null;
  // Precision #4 — attribution: the popup (if any) shown before the conversion
  // that plausibly rescued this visitor. null = organic conversion, no popup.
  rescuedByPopup: { popupId: string; name: string; trigger: string | null; shownAt: number } | null;
  // Engagement / risk rollups.
  riskPeak: number;
  totalPageViews: number;
  // Interest profile — time + visits per semantic page kind, across sessions.
  pageKinds: Array<{ kind: PageKind; visits: number; dwellMs: number }>;
  // The chronological path across sessions (capped). Each step carries its kind.
  steps: Array<{
    ts: number;
    pathname: string;
    kind: PageKind;
    sessionId: string;
    dwellMs: number;
    isNewSession: boolean;     // first step of a new session (visit boundary)
  }>;
  // What they typed into forms — the identifying / high-value fields, with the
  // operator-tagged role when known. Values are already captured + tagged.
  fields: Array<{
    label: string | null;
    role: FieldRole | null;
    value: string;
    pathname: string | null;
    observedAt: number;
  }>;
};

// Value #1 — broken-page detection. HIGH PRECISION by design: we only surface
// a suspected issue when it's corroborated and reproducible, never on raw
// counts. Every issue carries the EVIDENCE (rate, distinct visitors, sample
// recordings) so the operator verifies in replay — we suggest, they confirm.
export type PageHealthIssue = {
  kind: "dead" | "rage" | "error" | "network";
  target: string;            // selector (dead/rage) | message (error) | url (network)
  label: string | null;      // element text for dead/rage, for recognizability
  tag: string | null;        // element tag (dead/rage)
  sessions: number;          // distinct sessions exhibiting it
  visitors: number;          // distinct visitors (≥2 required — not one person raging)
  affectedRate: number;      // 0..100 of the page's sessions
  confidence: "high" | "medium";
  sampleSessionIds: string[]; // up to 3 recent — "watch it and verify"
  findingKey: string;         // stable id for the findings workflow (PATCH status)
  status: "open" | "confirmed" | "false_positive" | "fixed";
};
export type PageHealthRow = {
  pageHash: string;
  pathname: string;
  pageSessions: number;
  issues: PageHealthIssue[];
  // ≥2 distinct signal kinds agree on this page → strong "genuinely broken".
  corroborated: boolean;
};
export type PageHealthResponse = {
  windowDays: number;
  pages: PageHealthRow[];
};

// Zero-code conversion tracking. Site owner picks one or more URL glob
// patterns ("/thank-you", "/orders/*/confirmed") and the aggregator stamps
// any session whose visitor hits a matching page as converted. No code
// change required on the customer's site.
export type SiteConversionRules = {
  paths: string[]; // glob patterns, evaluated against pathname (no query)
};

// Success text patterns — conversion-by-content. When any pattern appears
// in the live DOM the tracker fires a cv event and the aggregator marks
// the session converted. For sites with dynamic URLs (UUIDs, SPA forms)
// this is the only way to detect conversion.
export type SiteSuccessRules = {
  patterns: string[];
};

// Conversion funnel — an ordered list of steps a visitor passes through.
export type FunnelStep = {
  label: string;
  match: string; // glob path (same semantics as conversion_paths); may be "" when kind is set
  // When set, the step matches ANY page CLASSIFIED as this kind — robust to
  // i18n and dynamic URLs (a checkout step catches /checkout, /קופה,
  // /order/confirm…) instead of a single recorded pathname.
  kind?: PageKind;
};
export type SiteFunnel = {
  id: string;
  siteId: string;
  name: string;
  steps: FunnelStep[];
  createdAt: number;
  updatedAt: number;
};
// Computed result — one row per step, cumulative (reached step k = reached
// all of 1..k in order). dropOff is how many were lost since the prior step.
export type FunnelResult = {
  funnelId: string;
  totalVisitors: number;   // entered step 1
  windowDays: number;
  steps: Array<{
    label: string;
    match: string;
    reached: number;       // distinct visitors who reached this step
    pctOfStart: number;    // 0..100 relative to step 1
    pctOfPrev: number;     // 0..100 relative to the previous step (step-to-step CR)
    droppedFromPrev: number;
  }>;
};

// Channel breakdown for a single page. Powers the small "who clicks here"
// card on the heatmap viewer — paid vs organic vs AI etc. at a glance.
export type PageSourceBreakdown = {
  totalSessions: number;
  rows: Array<{
    channel: string;          // direct | organic | paid | social | email | referral | ai | other
    referrerHost: string | null;
    sessions: number;
    pct: number;              // 0..100, rounded to one decimal
  }>;
};

// AI heatmap insight — one suggestion from the LLM. Severity drives the
// dashboard's icon/color; suggestion is the actionable line for the
// operator to act on.
export type HeatmapInsight = {
  severity: "info" | "warning" | "critical";
  title: string;
  observation: string;     // what the AI saw in the data
  suggestion: string;      // what to do about it
};
export type HeatmapInsightsResponse = {
  insights: HeatmapInsight[];
};

// Mirror of SiteConversionRules for the "this visitor is an employee, not
// a customer" tagging. Site owner lists admin/CMS/support URLs here; the
// aggregator marks any session that hits one as is_internal so the
// dashboard can exclude them from customer metrics and badge them.
export type SiteInternalRules = {
  paths: string[];
};

// Sessions whose last event arrived within the last 60s. Powers the "live
// now" strip at the top of per-site pages — a one-glance answer to
// "anyone on the site right now?" with a one-click jump to each session.
export type LiveSessionSummary = {
  id: string;
  visitorId: string;
  startedAt: number;
  lastEventAt: number;
  pageCount: number;
  signals: number;          // rage + dead + error + network combined
  currentPath: string | null;
  convertedAt: number | null;     // ms epoch of the first matching PV, or null
  conversionPath: string | null;  // which path triggered the conversion
  isInternal: boolean;            // true when an internal-path rule matched
};

export type PagesByRootRow = {
  rootPath: string;       // "/", "/products", "/blog" ...
  visits: number;         // total session_page rows in window
  avgDwellMs: number;     // averaged across rows with dwell > 0
  distinctPages: number;  // distinct page_hash values rolled into this root
};

export type HeatmapPoint = {
  // Stored in a fixed grid (see HEATMAP_GRID_PX). x,y are grid cell indices.
  x: number;
  y: number;
  weight: number;
};

export type HeatmapResponse = {
  pageHash: string;
  pathname: string;
  viewport: "desktop" | "tablet" | "mobile";
  gridPx: number;
  width: number;          // canonical doc width for this viewport bucket
  height: number;
  points: HeatmapPoint[];
  totalClicks: number;
  totalSessions: number;
  // Precision #2 — segmentation metadata. Present when this heatmap was
  // computed for a specific segment (converters vs not, source, page kind)
  // from a bounded raw sample rather than the all-time pre-aggregation.
  segment?: string;          // e.g. "converted" | "unconverted" | "channel:paid"
  sampleSessions?: number;   // how many sessions the sample was built from
};

// The segments a heatmap can be sliced by (Precision #2).
export type HeatmapSegment =
  | "all"
  | "converted"
  | "unconverted"
  | `channel:${string}`
  | `kind:${PageKind}`;

export type PageSnapshotResponse = {
  pathname: string;
  takenAt: number | null;
  viewportW: number | null;
  viewportH: number | null;
  // rrweb events — meta (type 4) followed by fullSnapshot (type 2).
  events: Array<{ type: number; data: unknown; timestamp: number }>;
};

export type SessionSummary = {
  id: string;
  visitorId: string;
  startedAt: number;
  durationMs: number;
  pathname: string;
  pageCount: number;
  clickCount: number;
  viewport: "desktop" | "tablet" | "mobile";
  hasReplay: boolean;
  // Frustration signals — drive the badges in the sessions list. Zero is
  // the common case so the UI should treat absence the same as a 0.
  rageClicks: number;
  deadClicks: number;
  errors: number;
  networkErrors: number;
  // Identity stitching — set once the host page calls hotmaps.identify(uid).
  identifiedUserId: string | null;
  // Pages this session passed through (for triage from the list).
  pages: string[];
  // The site/domain this session belongs to (shown next to the session id).
  domain: string | null;
  // How many sessions this browser has had (>1 ⇒ returning visitor).
  visitorSessionCount: number;
  // R1 — peak abandonment-risk score reached during the session (0-100), for
  // list badges + "nearly left" sorting. 0 when the engine never elevated.
  riskPeak: number;
  // Acquisition source — where THIS session came from, on every list row
  // (Idan 2026-09-08: the sessions list showed no source at all; the data
  // only lived in the session detail). channel matches classifyChannel.
  channel?: string | null;
  referrerHost?: string | null;
  utmSource?: string | null;
  utmCampaign?: string | null;
  landingPath?: string | null;
  // Purchase stage — the furthest page-type the visitor reached this session
  // (checkout > cart > product > products > … > other). Higher rank = more
  // advanced buying intent. Derived from page-types (owner override ?? guess).
  purchaseStage: PageType;
  purchaseStageRank: number;
  // Over-quota lock: the session is recorded and kept, but inaccessible to the
  // customer (newest bytes beyond the storage quota) until they free space by
  // deleting old sessions or upgrade. Undefined/false = accessible.
  locked?: boolean;
};

// Cloudflare unit pricing (USD), single source of truth for cost estimates.
// Update here if Cloudflare changes pricing. R2 egress is free (not listed).
export const CF_PRICING = {
  r2StoragePerGBMonth: 0.015,
  r2ClassAPerMillion: 4.5, // writes (PUT/LIST)
  r2ClassBPerMillion: 0.36, // reads (GET/HEAD)
  workerReqPerMillion: 0.3,
  queueOpsPerMillion: 0.4,
  d1RowWrittenPerMillion: 1.0,
};

// Cost of one recorded session, computed from real R2 object sizes.
export type SessionCost = {
  chunks: number; // raw event chunks in R2
  bytesRaw: number; // raw chunks total bytes
  bytesReplay: number; // consolidated gzipped replay bytes (0 if not built yet)
  bytesTotal: number;
  storageMonthlyUsd: number; // ongoing, per month at rest
  ingestOneTimeUsd: number; // capture-time ops (writes, requests, queue, d1)
  perReplayViewUsd: number; // each playback (≈1 GET once consolidated)
  firstMonthUsd: number; // storageMonthly + ingest + one view — a round figure
};

// Cloudflare monthly included/free allowances (beyond the $5 Workers Paid base).
// These bound how much you can record at no extra cost.
export const CF_FREE_LIMITS = {
  r2StorageGB: 10,
  r2ClassAPerMonth: 1_000_000, // R2 writes
  r2ClassBPerMonth: 10_000_000, // R2 reads
  workerReqPerMonth: 10_000_000,
  queueOpsPerMonth: 1_000_000,
};

export type SiteCostRow = {
  siteId: string;
  domain: string | null;
  sessions: number; // all-time
  chunks: number; // all-time
  bytesStored: number; // all-time
  monthSessions: number;
  monthChunks: number;
  storageMonthlyUsd: number;
  captureMonthUsd: number;
  totalMonthlyUsd: number;
};

// Account-wide + per-site cost and free-tier headroom.
export type CostSummary = {
  // This calendar month (capture + headroom are monthly; storage is all-time).
  monthSessions: number;
  monthChunks: number;
  totalSessions: number;
  totalBytesStored: number;
  avgBytesPerSession: number;
  avgChunksPerSession: number;
  // Cost (USD).
  captureMonthUsd: number; // ops to capture this month's sessions
  storageMonthlyUsd: number; // ongoing storage at rest
  totalMonthlyUsd: number;
  // Free-tier usage this month + remaining headroom.
  usage: {
    r2WritesUsed: number; r2WritesFree: number;
    queueOpsUsed: number; queueOpsFree: number;
    storageBytesUsed: number; storageBytesFree: number;
  };
  freeSessionsRemaining: number; // more sessions you can record this month at $0 extra
  bindingLimit: string; // which limit binds first
  sites: SiteCostRow[];
};

// Per-site new-vs-returning visitor summary.
export type VisitorsSummary = {
  days: number;
  totalVisitors: number;
  newVisitors: number;
  returningVisitors: number;
  identifiedVisitors: number;
  totalSessions: number;
};

// Full single-session detail. Returned by /sites/:siteId/sessions/:sessionId.
// Strictly a superset of SessionSummary — the player page fetches this once
// instead of looking the session up in the list response.
export type SessionDetail = {
  id: string;
  siteId: string;
  domain: string | null;
  visitorId: string;
  startedAt: number;
  lastEventAt: number;
  durationMs: number;
  pageCount: number;
  clickCount: number;
  eventCount: number;
  viewport: "desktop" | "tablet" | "mobile";
  viewportW: number | null;
  viewportH: number | null;
  userAgent: string | null;
  hasReplay: boolean;
  rageClicks: number;
  deadClicks: number;
  errors: number;
  networkErrors: number;
  // Engagement totals.
  moveCount: number;
  touchCount: number;
  maxScrollPct: number;
  // Acquisition — how this visitor arrived.
  channel: SourceChannel | null;
  referrerHost: string | null;
  referrerUrl: string | null;
  landingPath: string | null;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    term: string | null;
    content: string | null;
  } | null;
  // Client/device fingerprint (from the tracker).
  client: {
    sw?: number; sh?: number; dpr?: number;
    lang?: string; langs?: string; tz?: string; plat?: string;
    cores?: number; mem?: number; conn?: string; touch?: number;
  } | null;
  // Identity stitching — the system user this visitor was identified as.
  identifiedUserId: string | null;
  identifiedAt: number | null;
  traits: Record<string, string | number | boolean> | null;
  // Geo + IP (from the Cloudflare edge — request.cf / CF-Connecting-IP).
  geo: {
    ip?: string;
    country?: string; city?: string; region?: string;
    org?: string; colo?: string; tz?: string;
    lat?: string; lon?: string;
  } | null;
  // New vs returning: false = this browser's first session, true = seen before.
  returning: boolean | null;
  // How many sessions this visitor (browser) has had, in this tenant.
  visitorSessionCount: number;
  // Pages visited during this session, in first-seen order, with time-on-page.
  // U1 — each step carries its semantic page kind (for the Customer Journey).
  journey: Array<{ pageHash: string; pathname: string; firstAt: number; dwellMs?: number; kind?: PageKind }>;
  // R1 — risk rollup for the header badge. Full timeline is a separate fetch.
  riskPeak: number;
  riskLast: number | null;
};

// Source / acquisition aggregates per site.
export type SourceChannel =
  | "direct"
  | "organic"
  | "ai"        // referred by an AI assistant (ChatGPT/Claude/Gemini/Perplexity/…)
  | "paid"
  | "social"
  | "email"
  | "referral"
  | "other";

export type SourcesResponse = {
  // Total sessions in the window (so the dashboard can compute % share).
  totalSessions: number;
  // Aggregated by channel (the high-level overview).
  channels: Array<{ channel: SourceChannel; sessions: number }>;
  // Top referrer hosts (excluding empty/direct).
  referrers: Array<{ host: string; sessions: number }>;
  // Top UTM combinations.
  campaigns: Array<{
    source: string | null;
    medium: string | null;
    campaign: string | null;
    sessions: number;
  }>;
  // Top landing pages (where sessions enter the site).
  landings: Array<{ path: string; sessions: number }>;
};

// One row per detected signal — used by the player timeline to drop markers.
// A deterministic, rule-based read of one signal — the "why + how to fix"
// that the player overlays and that the AI scanner escalates from. Language is
// Hebrew (owner-facing). severity drives the marker color / sort order.
export type SignalDiagnosis = {
  code: string;                 // stable machine tag, e.g. "dead.image", "net.4xx"
  title: string;                // short human label ("תמונה לא לחיצה")
  where: string;                // exactly WHERE, in words ("הכפתור 'אבקת חלבון מומלצת' בדף המלצות")
  why: string;                  // why this is a problem for the visitor
  fix: string;                  // concrete remediation
  reproduce: string;            // exact steps to see it yourself
  severity: "high" | "medium" | "low";
};

export type SessionSignal = {
  id: number;
  ts: number;
  // "custom" = a host-page hotmaps.track(...) call; payload is {name, props}.
  kind: "rage" | "dead" | "error" | "network_error" | "custom";
  pageHash: string | null;
  payload: Record<string, unknown>;
  // --- enrichment (server-computed; optional so old callers still parse) ---
  diagnosis?: SignalDiagnosis;
  pathname?: string | null;     // page the signal fired on
  pageUrl?: string | null;      // exact absolute URL to reproduce on
  sessionsAffected?: number;    // DISTINCT sessions site-wide with this same fault
  x?: number | null;            // click coords (page space) for the over-video pin
  y?: number | null;
};

// One clustered, diagnosed problem on a site (GET /sites/:id/findings). The
// recurring-bug feed behind the Problems page: each is a cluster of ≥3 sessions
// hitting the same fault, carried with the shared where·why·fix·reproduce
// diagnosis, its exact page URL, and a sample session to watch it in replay.
export type SiteFinding = {
  key: string;
  kind: "rage" | "dead" | "error" | "network_error";
  target: string | null;       // selector / message / status+url
  tag: string | null;
  txt: string | null;          // clicked element's text, when any
  pathname: string | null;
  pageUrl: string | null;      // exact URL to reproduce on
  sessions: number;            // DISTINCT sessions hit (blast radius)
  sampleSessionId: string;     // watch it in the replay (rings the element)
  lastSeen: number;            // epoch ms of most recent occurrence
  diagnosis: SignalDiagnosis;
  status: "open" | "confirmed" | "false_positive" | "fixed";
  note: string | null;
};
export type SiteFindingsResponse = { windowDays: number; findings: SiteFinding[] };

export const HEATMAP_GRID_PX = 10 as const;

// --- Signal diagnosis (deterministic "why + where + fix + reproduce") -------
// One shared brain used by the API (per-session overlay), the batch scanner,
// and the dashboard. Pure + rule-based → runs on EVERY site, zero tokens, and
// produces the STRUCTURED tags an AI agent (Gemini) later summarises into
// higher-level conclusions. Never says only "there's a problem": it names the
// exact element, why it hurts, how to fix, and how to reproduce it.
function humanizePath(pathname?: string | null): string {
  if (!pathname) return "הדף";
  try {
    const dec = decodeURIComponent(pathname);
    const last = dec.replace(/\/+$/, "").split("/").filter(Boolean).pop();
    return last ? `הדף "${last.replace(/[-_]/g, " ")}"` : "דף הבית";
  } catch { return `הדף ${pathname}`; }
}

export function diagnoseSignal(
  kind: SessionSignal["kind"],
  payload: Record<string, unknown>,
  opts?: { pathname?: string | null }
): SignalDiagnosis {
  const sel = String(payload.sel ?? "");
  const tag = String(payload.tag ?? "").toLowerCase();
  const txt = String(payload.txt ?? "").trim();
  const where0 = humanizePath(opts?.pathname);
  const label = txt ? `"${txt.slice(0, 40)}"` : sel ? `\`${sel.slice(0, 50)}\`` : "האלמנט";
  const seeItSelf = `פתח/י את ${where0}, גלול/י אל ${label} ולחץ/י עליו — לא יקרה כלום. או לחצ/י "שחזר" כאן כדי לראות את הגולש עושה זאת בהקלטה.`;

  if (kind === "dead") {
    const isImg = tag === "img" || /\bimg\b|image|attachment|carousel-image/.test(sel);
    const isElementorBtn = /elementor-button/.test(sel);
    const isHeading = /^h[1-6]$/.test(tag) || /heading-title|\bh3\b/.test(sel);
    const isIconBox = /icon-box/.test(sel);
    if (isElementorBtn) return {
      code: "dead.button_no_link",
      title: "כפתור ללא קישור פעיל",
      where: `הכפתור ${label} ב${where0}`,
      why: "המבקר לחץ על כפתור פעולה (CTA) ולא הופנה לשום מקום — פעולה/המרה אבודה.",
      fix: "ב-Elementor: בחר/י את הכפתור → Content → Link, והזן/י את היעד (קישור מוצר / וואטסאפ / דף). אם זו לחיצת פופאפ — ודא/י שה-Dynamic Action של הפופאפ מחובר לכפתור.",
      reproduce: seeItSelf,
      severity: "high",
    };
    if (isImg) return {
      code: "dead.image",
      title: "תמונה שנראית לחיצה אך אינה מקושרת",
      where: `התמונה ${label || ""} ב${where0}`.trim(),
      why: "מבקרים מצפים שתמונת מוצר/המלצה תוביל למוצר. הלחיצה מתה → הכנסה (אפיליאט/מכירה) אבודה.",
      fix: "עטוף/י את ה-Image widget בקישור (Elementor: Image → Link → URL של המוצר), או הסר/י את מראה הלחיצוּת (cursor:pointer) אם היא לא אמורה להיות לחיצה.",
      reproduce: seeItSelf,
      severity: "high",
    };
    if (isHeading) return {
      code: "dead.heading",
      title: "כותרת נלחצת כאילו היא קישור",
      where: `הכותרת ${label} ב${where0}`,
      why: "המבקר תפס את הכותרת כקישור. פער ציפיות → תסכול ונטישה.",
      fix: "אם הכותרת אמורה להוביל למקום — הפוך/י אותה לקישור. אחרת הפחת/י רמזי-לחיצוּת (הדגשה/צבע קישור) כדי לא להטעות.",
      reproduce: seeItSelf,
      severity: "medium",
    };
    if (isIconBox) return {
      code: "dead.icon_box",
      title: "תיבת אייקון/תיאור לא לחיצה",
      where: `תיבת התוכן ${label} ב${where0}`,
      why: "התיבה כולה נראית לחיצה אך רק חלק ממנה (או כלום) מקושר.",
      fix: "ב-Elementor הגדר/י Link על כל ה-Icon Box (לא רק על האייקון), או הסר/י את מראה הלחיצוּת.",
      reproduce: seeItSelf,
      severity: "medium",
    };
    return {
      code: "dead.generic",
      title: "אלמנט שאינו מגיב ללחיצה",
      where: `${label} ב${where0}`,
      why: "המבקר לחץ ולא קרה כלום — סימן לציפייה שלא נענתה.",
      fix: "בדוק/י אם האלמנט אמור להיות לחיץ; אם כן — חבר/י קישור/פעולה. אם לא — הסר/י רמזי לחיצוּת.",
      reproduce: seeItSelf,
      severity: "medium",
    };
  }

  if (kind === "rage") return {
    code: "rage.clicks",
    title: "לחיצות זעם — לחיצות חוזרות ומהירות",
    where: `${label} ב${where0}`,
    why: "המבקר לחץ שוב ושוב באותו מקום — האלמנט איטי, תקוע, או לא מגיב. תסכול גבוה.",
    fix: "בדוק/י מדוע האלמנט לא מגיב מיד: קישור שבור, handler איטי, או אלמנט שנראה לחיץ ואינו. הבטח/י תגובה מיידית ללחיצה.",
    reproduce: `פתח/י את ${where0} ולחצ/י מספר פעמים על ${label} — הוא לא יגיב כמצופה. או לחצ/י "שחזר".`,
    severity: "high",
  };

  if (kind === "error") {
    const msg = String(payload.message ?? payload.msg ?? "").slice(0, 140) || "שגיאת JavaScript";
    return {
      code: "error.js",
      title: "שגיאת JavaScript בדף",
      where: `${where0} (בזמן הצפייה של המבקר)`,
      why: `סקריפט קרס: "${msg}". עלול לשבור כפתורים, טפסים או תהליך רכישה.`,
      fix: "פתח/י את הדף עם DevTools → Console, שחזר/י את הפעולה וראה/י את השגיאה המלאה (קובץ+שורה). תקן/י את הסקריפט או הרחבה שגורמת לה.",
      reproduce: `פתח/י את ${where0} עם קונסול פתוח ובצע/י את מסלול המבקר; השגיאה תופיע. או לחצ/י "שחזר" לראות את הרגע.`,
      severity: "high",
    };
  }

  if (kind === "network_error") {
    const status = Number(payload.status ?? 0);
    const url = String(payload.url ?? "");
    const short = url.replace(/^https?:\/\//, "").slice(0, 60);
    // Third-party ad / analytics / tag scripts fail constantly and are NOT the
    // site's code — they must not drown the owner's real problems. Flag low and
    // say so plainly, so ranking drops them below anything actionable.
    const THIRD_PARTY = /doubleclick|googlesyndication|google-analytics|googletagmanager|gtag\/|facebook\.|fbcdn|connect\.facebook|azurefd\.net|adsagent|\/msba\/|hotjar|clarity\.ms|taboola|outbrain|tiktok|snapchat|criteo|bing\.com|clarity|cdn\.segment|fullstory/i;
    if (THIRD_PARTY.test(url)) return {
      code: "net.thirdparty",
      title: "כשל בסקריפט צד-שלישי (פרסום/אנליטיקס)",
      where: `${where0} · ${short}`,
      why: "משאב של שירות חיצוני (פרסום/מדידה) נכשל — לא קוד של האתר עצמו. בדרך כלל לא משפיע על חוויית הרכישה, אך מלכלך את הקונסול.",
      fix: "ברוב המקרים אפשר להתעלם. אם חוזר הרבה — לבדוק את הגדרת הסקריפט החיצוני (חוסם פרסומות, תג שלא נטען, מפתח שגוי) או להסירו.",
      reproduce: `פתח/י את ${where0} עם DevTools → Network וסנן/י ל-${short}; תראה/י אותו נכשל.`,
      severity: "low",
    };
    return {
      code: status >= 500 ? "net.5xx" : "net.4xx",
      title: `קריאת רשת נכשלה${status ? ` (${status})` : ""}`,
      where: `${where0} · ${short}`,
      why: status === 404
        ? "משאב חסר (404) — תמונה/סקריפט/API שלא קיים. עלול לשבור תצוגה או פעולה."
        : status >= 500
        ? "השרת החזיר שגיאה (5xx) — שירות צד-שרת נפל."
        : "קריאת רשת נכשלה — ייתכן שפעולה בדף לא הושלמה.",
      fix: status === 404
        ? `תקן/י או הסר/י את ההפניה ל-${short}.`
        : "בדוק/י את ה-endpoint בצד השרת/הספק ואת ה-CORS; ודא/י שהוא מחזיר 200.",
      reproduce: `פתח/י את ${where0} עם DevTools → Network, סנן/י לפי הכשל; תראה/י את ${short} נכשל. או לחצ/י "שחזר".`,
      severity: status >= 500 || status === 404 ? "high" : "medium",
    };
  }

  return {
    code: "custom.event",
    title: "אירוע מותאם",
    where: where0,
    why: "אירוע שנשלח מהאתר (hotmaps.track).",
    fix: "—",
    reproduce: `לחצ/י "שחזר" כדי לראות את הרגע בהקלטה.`,
    severity: "low",
  };
}

export function viewportBucket(width: number): "desktop" | "tablet" | "mobile" {
  if (width >= 1024) return "desktop";
  if (width >= 640) return "tablet";
  return "mobile";
}

// --- URL classification (page_kind) ---------------------------------------
// Every pathname is classified into one semantic bucket. This is the
// foundation the later layers reason over ("this is a checkout page" vs
// "a content page"). Deterministic + pure so the tracker, aggregator, API
// and dashboard all agree — single source of truth lives HERE.
//
// `home` is its own bucket (the landing root carries distinct intent from a
// generic "other" page). Precedence matters: auth/checkout/account win over
// product/service/content because a "/account/orders/123" page is an account
// page even though it contains "orders". Multilingual needles (he/en/ar)
// because most HotMaps customers operate in those markets.

export type PageKind =
  | "home"
  | "product"
  | "service"
  | "content"
  | "account"
  | "checkout"
  | "auth"
  | "other";

export const PAGE_KINDS: PageKind[] = [
  "home", "product", "service", "content", "account", "checkout", "auth", "other",
];

// Each rule is tested in order; first hit wins. Needles match anywhere in the
// lowercased pathname unless anchored. Kept conservative — we'd rather fall
// through to "other" than mis-bucket.
const PAGE_KIND_RULES: Array<{ kind: PageKind; re: RegExp }> = [
  // Auth — login/registration/SSO/password flows. Highest precedence: a
  // "/account/login" is auth, not account.
  { kind: "auth", re: /(^|\/)(login|signin|sign-in|sign_in|log-in|logout|signup|sign-up|register|registration|auth|sso|oauth|password|forgot|reset-password|verify|otp|מתחבר|הרשמה|התחברות|כניסה|تسجيل|دخول)(\/|$)/ },
  // Checkout — the money funnel. Wins over account/product.
  { kind: "checkout", re: /(^|\/)(checkout|cart|carts|basket|bag|payment|pay|billing|order\/(confirm|review|complete)|thank-?you|order-confirmation|תשלום|עגלה|סל|קופה|הזמנה|الدفع|السلة|عربة)(\/|$)/ },
  // Account — the logged-in self-service area.
  { kind: "account", re: /(^|\/)(account|accounts|profile|profiles|dashboard|settings|preferences|my-?account|orders|order-history|wishlist|favorites|subscriptions|membership|inbox|messages|notifications|חשבון|הגדרות|הזמנות|פרופיל|الحساب|الملف|الطلبات)(\/|$)/ },
  // Product — catalog item / shop pages.
  { kind: "product", re: /(^|\/)(product|products|prod|p|item|items|sku|shop|store|catalog|catalogue|collection|collections|category|categories|buy|deal|deals|מוצר|מוצרים|חנות|קטלוג|קטגוריה|منتج|منتجات|متجر|تسوق)(\/|$)/ },
  // Service — service offerings + pricing/plans (intent to evaluate cost).
  { kind: "service", re: /(^|\/)(service|services|pricing|prices|price|plan|plans|packages?|tariffs?|quote|booking|book|appointment|reserve|reservation|שירות|שירותים|תמחור|מחירון|מחירים|חבילות|הזמנת-תור|خدمة|خدمات|الأسعار|باقات|حجز)(\/|$)/ },
  // Content — editorial/info/support reading pages.
  { kind: "content", re: /(^|\/)(blog|blogs|article|articles|news|press|post|posts|story|stories|guide|guides|docs?|documentation|help|support|faq|faqs|about|about-us|contact|contact-us|terms|privacy|policy|legal|resources?|tutorial|tutorials|case-stud|בלוג|מאמר|חדשות|מדריך|עזרה|אודות|צור-קשר|תקנון|פרטיות|مدونة|مقال|أخبار|مساعدة|دعم|من-نحن|اتصل)(\/|$)/ },
];

// --- URL normalization / templating (Precision #1) ------------------------
// Dynamic URLs (/product/123, /orders/4827, /medical/<uuid>) fragment heatmaps
// and page analytics into hundreds of near-empty buckets. normalizePath folds
// the volatile segments into stable tokens so /product/123 and /product/456
// share ONE heatmap/template — the aggregator keys pages + heatmap cells by the
// templated hash, which is what finally makes heatmaps readable and conversion
// paths precise.
//
// Deliberately CONSERVATIVE: we only template segments that are unmistakably
// identifiers (pure numeric, UUID, long hex, Mongo ObjectId, opaque tokens).
// Human-readable slugs (/blog/my-post, /about-us, /product/red-shoes) are left
// intact — they usually carry distinct content, and over-templating would
// collapse genuinely different pages.
// Parent segments under which a numeric-prefixed slug is treated as a product id.
const PRODUCT_PARENT = /^(product|products|prod|p|item|items|sku|shop|store|catalog|catalogue|collection|collections|category|categories|deal|deals)$/i;

export function normalizePath(pathname: string | null | undefined): string {
  if (!pathname) return "/";
  let p = pathname.split("?")[0]!.split("#")[0]!;
  if (!p.startsWith("/")) p = "/" + p;
  const segs = p.split("/");
  const out = segs.map((seg, idx) => {
    if (!seg) return seg;
    if (/^\d+$/.test(seg)) return ":id";                         // 123, 4827
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ":uuid";
    if (/^[0-9a-f]{24}$/i.test(seg)) return ":id";               // Mongo ObjectId
    if (/^[0-9a-f]{16,}$/i.test(seg)) return ":hash";            // long hex digest
    // Numeric-prefixed slug ("123-red-shoes" → :id) — ONLY under a product-like
    // parent (/product/123-red-shoes). This deliberately spares content slugs
    // that also start with a number, e.g. /blog/2024-recap or /2024-report.
    if (/^\d+-[a-z0-9-]+$/i.test(seg) && PRODUCT_PARENT.test(segs[idx - 1] ?? "")) return ":id";
    // Opaque token: long, mixes letters AND digits, no hyphen (base64-ish ids,
    // session tokens). Keeps real words (all-letters) and short codes intact.
    if (seg.length >= 12 && /[a-z]/i.test(seg) && /\d/.test(seg) && /^[a-z0-9_]+$/i.test(seg)) return ":token";
    return seg;
  });
  const t = out.join("/");
  return t === "" ? "/" : t;
}

// FNV-1a 32-bit → 8 hex chars. IDENTICAL to the tracker's hashPath so a template
// hash computed here lines up with the tracker's raw-path hashes byte-for-byte.
// The aggregator uses this to key pages/heatmaps by the normalized template.
export function hashPath(p: string): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < p.length; i++) {
    h ^= p.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function classifyPageKind(pathname: string | null | undefined): PageKind {
  if (!pathname) return "other";
  // Strip query/hash and lowercase; ensure a trailing context char so
  // "/cart" (no trailing slash) still matches the "(\/|$)" anchor.
  let p = pathname.split("?")[0]!.split("#")[0]!.toLowerCase();
  if (!p.startsWith("/")) p = "/" + p;
  // CRITICAL: decode percent-encoding. Hebrew/Arabic WordPress URLs arrive as
  // "/%d7%a2%d7%92%d7%9c%d7%94/" — without decoding, the non-Latin rules below
  // can never match and every such page lands in "other".
  try { p = decodeURIComponent(p); } catch { /* malformed escape — keep raw */ }
  // Home — exactly "/" or empty-ish roots ("/index", "/home").
  if (p === "/" || p === "" || /^\/(index|home|דף-הבית|الرئيسية)(\.\w+)?\/?$/.test(p)) {
    return "home";
  }
  for (const { kind, re } of PAGE_KIND_RULES) {
    if (re.test(p)) return kind;
  }
  // WordPress date-archive articles: /2026/07/29/slug/ — and the same path
  // after template normalization, /:id/:id/:id/slug/. Editorial content.
  if (/^\/((19|20)\d{2}\/\d{1,2}\/\d{1,2}|:id\/:id\/:id)\//.test(p)) return "content";
  // Second pass — loose token match. Real-world slugs bury the signal word
  // inside a hyphenated segment ("biochange-תוכנית-ליווי-אישית",
  // "product-category", "yoga-workshop-tlv") where the strict full-segment
  // rules above can't reach. Split on slug separators and match single tokens.
  const tokens = p.split(/[/\-_+.]+/).filter(Boolean);
  for (const tk of tokens) {
    const kind = LOOSE_TOKEN_KINDS[tk];
    if (kind) return kind;
  }
  return "other";
}

// Loose single-token → kind map for the second classification pass. Only words
// that are unambiguous on their own (in any position inside a slug) belong
// here — one-letter/short generics like "p"/"pay" stay strict-only above.
const LOOSE_TOKEN_KINDS: Record<string, PageKind> = {
  // checkout
  "עגלה": "checkout", "קופה": "checkout", "תשלום": "checkout", "checkout": "checkout",
  // product / catalog
  "מוצר": "product", "מוצרים": "product", "חנות": "product", "קטלוג": "product",
  "קטגוריה": "product", "קורס": "product", "קורסים": "product", "תוספים": "product",
  "course": "product", "courses": "product", "category": "product", "shop": "product",
  // service / programs / offerings
  "שירות": "service", "שירותים": "service", "תוכנית": "service", "תכנית": "service",
  "תוכניות": "service", "ליווי": "service", "סדנה": "service", "סדנאות": "service",
  "אימון": "service", "ייעוץ": "service", "טיפול": "service", "טיפולים": "service",
  "בדיקות": "service", "בדיקה": "service", "אתגר": "service", "מחירון": "service",
  "workshop": "service", "webinar": "service", "program": "service", "pricing": "service",
  // content / editorial / community
  "בלוג": "content", "מאמר": "content", "מאמרים": "content", "מדריך": "content",
  "מדריכים": "content", "חדשות": "content", "אודות": "content", "קהילה": "content",
  "פודקאסט": "content", "וידאו": "content", "סרטון": "content", "המלצות": "content",
  "blog": "content", "podcast": "content", "community": "content", "faq": "content", "tag": "content",
};

// --- Abandonment risk (R1) -------------------------------------------------
// Shared band thresholds so the tracker, popup trigger engine and dashboard
// all agree on what "high risk" means. A score is 0-100.
import type { RiskLevel } from "./events.js";

export const RISK_BANDS: Array<{ level: RiskLevel; min: number }> = [
  { level: "critical", min: 85 },
  { level: "high", min: 60 },
  { level: "medium", min: 35 },
  { level: "low", min: 0 },
];

export function riskLevel(score: number): RiskLevel {
  for (const b of RISK_BANDS) if (score >= b.min) return b.level;
  return "low";
}

// Human-friendly labels for the known risk factor keys the tracker emits.
// Kept here (not in the tracker) so the dashboard can render them without
// importing tracker internals.
export const RISK_FACTOR_KEYS = [
  "exit_velocity",   // cursor racing toward the top edge (exit precursor)
  "scroll_reversal", // big upward scroll after going deep (sticker shock / flee)
  "idle",            // went idle after being active
  "rage",            // rage clicks
  "dead",            // dead clicks
  "error",           // JS error this session
  "network",         // failed network request
  "field_abandon",   // focused a field, typed, then left without submitting
  "engagement_decay",// interaction rate falling off
  "rapid_back",      // rapid history back / nav churn
  // R3 — checkout-specific moat signals (highest predictiveness).
  "coupon_hunt",        // opened a coupon/promo field (often leaves to hunt a code)
  "payment_hesitation", // long dwell on a card/payment field with no progress
] as const;
export type RiskFactorKey = (typeof RISK_FACTOR_KEYS)[number];

// Per-session risk rollup (R1). Stored on recording_sessions + exposed in the
// session detail + a timeline for the replay overlay.
export type SessionRiskPoint = {
  ts: number;            // absolute epoch ms
  score: number;         // 0-100
  level: RiskLevel;
  factors: string[];     // top factor keys at this sample
  pageHash: string | null;
};
export type SessionRiskTimeline = {
  sessionId: string;
  peakScore: number;
  peakLevel: RiskLevel;
  lastScore: number;
  points: SessionRiskPoint[];
};

// --- API keys --------------------------------------------------------------

// 'partner' keys can provision tenants on behalf of a third-party platform
// (see Partner API section below). Cannot be minted from the dashboard;
// only seeded by admins via D1 (operations-grade trust required).
export type ApiKeyScope = "read" | "write" | "partner";

// What the GET /api-keys list returns. Never includes the secret — the
// dashboard reveals it exactly once, in the create response.
export type ApiKeySummary = {
  id: string;
  name: string;
  prefix: string;              // e.g. "hm_live_aB3" — for visual recognition
  scope: ApiKeyScope;
  siteId: string | null;       // null = tenant-wide
  lastUsedAt: number | null;
  createdAt: number;
};

// POST /api-keys response. The `key` field is the plaintext — show it ONCE
// then drop it from any UI state. Subsequent GETs return only ApiKeySummary.
export type ApiKeyCreated = {
  summary: ApiKeySummary;
  key: string;                 // the full secret. Never returned again.
};

export type CreateApiKeyRequest = {
  name: string;
  scope: ApiKeyScope;
  siteId?: string | null;      // omit / null for tenant-wide
};

export type UpdateApiKeyRequest = {
  name: string;                // rename is the only mutable field
};

// --- Partner API -----------------------------------------------------------
// Third-party platforms (site builders, SaaS suites, agencies) provision
// HotMaps tenants on behalf of their own customers. Authenticated with a
// single `partner` scope API key.

export type PartnerTenant = {
  tenantId: string;
  name: string;
  externalRef: string | null;   // the platform's own customer id; opaque to us
  siteCount: number;
  createdAt: number;
};

export type CreatePartnerTenantRequest = {
  name: string;                 // e.g. "Acme Customer #4827"
  externalRef?: string;         // your customer id; lets you correlate later
};

export type PartnerSite = {
  id: string;
  tenantId: string;
  domain: string;
  name: string;
  verified: boolean;
  createdAt: number;
};

export type CreatePartnerSiteRequest = {
  domain: string;
  name?: string;
  autoVerify?: boolean;         // partners are trusted; default true
  // true = also enable full console.log/info/warn capture on the site
  // (module config {"console":true}). Recording itself is always enabled.
  console?: boolean;
};

// Shape of the snippet a partner platform pastes into their customer's
// site. The `html` is ready to inject as-is; the parts are exposed so the
// platform can also template it however it wants.
export type SiteSnippet = {
  /** Full HTML snippet, ready to paste inside <head>. */
  html: string;
  /** The script src URL. */
  src: string;
  /** The data-site value. */
  dataSite: string;
  /** Human-friendly placement note for embed-snippet UIs. */
  placement: string;
};

export type PartnerSiteWithSnippet = PartnerSite & { snippet: SiteSnippet };

// --- PROVIDER mode ---------------------------------------------------------
// A provider is a partner that doesn't just mint tenants/sites, but manages
// the full account lifecycle for its customers: dashboard USERS, tenant-
// scoped API KEYS, and a one-call idempotent PROVISION that other systems
// (CALLSAI / MailOS / PAYWAYS-style skills) can wire against.

export type PartnerUser = {
  id: string;
  tenantId: string;
  email: string;
  role: "owner" | "admin" | "viewer";
  createdAt: number;
  lastLoginAt: number | null;
};

export type CreatePartnerUserRequest = {
  email: string;
  // Omit to have HotMaps generate a strong one — returned ONCE in the
  // response so the provider can hand it to their customer.
  password?: string;
  role?: "owner" | "admin" | "viewer";
};

export type PartnerUserCreated = PartnerUser & {
  // Present ONLY when the password was generated server-side.
  oneTimePassword?: string;
  loginUrl: string;
};

export type CreatePartnerApiKeyRequest = {
  name?: string;
  scope: "read" | "write";
  // Optional: scope the key to ONE site in the tenant. The key then only
  // works on /sites/:siteId/* routes for that site — what external bug
  // tooling should hold (read + siteId → replay/console/signals only).
  siteId?: string | null;
};

export type PartnerApiKeyCreated = {
  id: string;
  tenantId: string;
  siteId: string | null;
  name: string;
  scope: "read" | "write";
  // The full key — shown ONCE, store it on your side.
  key: string;
  createdAt: number;
};

// One-call provision. Idempotent by (partner key, project): the tenant is
// found-or-created by externalRef=project; the site by domain; the user by
// email. An api key is minted on EVERY call (previous ones stay valid) so
// callers always leave with a working credential — same contract as the
// sibling CALLSAI/MailOS provision flows.
export type PartnerProvisionRequest = {
  project: string;              // your stable id for this customer/project
  name?: string;                // tenant display name (default = project)
  domain?: string;              // optional: also ensure a site
  email?: string;               // optional: also ensure a dashboard user
  password?: string;            // used only if the user is created now
  issueApiKey?: boolean;        // default true
};

export type PartnerProvisionResponse = {
  tenant: PartnerTenant;
  created: { tenant: boolean; site: boolean; user: boolean };
  site: PartnerSiteWithSnippet | null;
  user: PartnerUserCreated | null;
  apiKey: PartnerApiKeyCreated | null;
  dashboardUrl: string;
  apiBase: string;
};
